import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router";
import SearchInput from "../../../components/shared/SearchInput";
import {
  useGetAllUserCompanies,
  useGetUserContext,
} from "../../../queries/auth";
import { globalFilter } from "../../../utils/arrays";
import { AssociatedCompany } from "../../../types/UserDetailModel";
import { useAuthStore } from "../../../store";
import { useGraphLoaderStore } from "../../../store/graphLoaderStore";
import { queryClient } from "../../../queries";
import { useShareholderInstrumentStore } from "../../../store/shareholderInstrumentStore";
import { useInternalSwitchStateStore } from "../../../store/searchDropDown";
import { useSelectedSecurityTypeStoreStore } from "../../../store/selectedSecurityTypeStore";
import { switchCompanyApi } from "../../../api/auth";
import { setUserProperTiesOnFirebase } from "../../../firebaseAnalytics";
import useIsMobile from "../../../utils/detectDevice";

export type CompanyType = {
  name: string;
  dba: string;
  id: string;
};
export const SwitchCompanyDropdown: React.FC<any> = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [enable, setEnable] = useState(false);

  const { setAuth, role, user, clear } = useAuthStore();
  const { data: userContext } = useGetUserContext(enable);
  const { data: companyData } = useGetAllUserCompanies();
  const graphStore = useGraphLoaderStore();
  const shareholderInstrument = useShareholderInstrumentStore();
  const transactionList = useInternalSwitchStateStore();
  const securityType = useSelectedSecurityTypeStoreStore();
  const navigate = useNavigate();

  const pathname = useLocation().pathname;

  const filteredCompanies = React.useMemo(() => {
    if (!companyData) return [];
    const filterResult = globalFilter(companyData, searchTerm, [
      "nameOfTheCompany",
      "dba",
    ]);
    return filterResult;
  }, [searchTerm, companyData]);

  useEffect(() => {
    setUserProperTiesOnFirebase(user?.fullName || "", user?.nameOfTheCompany);
  }, [user, userContext]);

  const goBack = () => {
    if (pathname.includes("planView")) {
      navigate("/options/allPlans");
    } else if (pathname.includes("employeeDetails")) {
      navigate("/options/allEmployees");
    } else if (
      pathname.includes("rights/new") ||
      pathname.includes("rights/shareholder")
    ) {
      navigate("/rights/overview");
    } else if (pathname.includes("shareholders/individual")) {
      navigate("/shareholders/overview");
    } else if (pathname.includes("security/individual")) {
      navigate("/security/overview");
    } else if (
      pathname.includes("addNewSecurity") ||
      pathname.includes("securityClass")
    ) {
      securityType.setSecurityType("All");
      navigate("/security/overview");
    } else if (pathname.includes("termsheet")) {
      navigate("/termsheet/overview");
    } else if (pathname.includes("rights/overview")) {
      navigate("/transactions/allTransactions");
    } else if (pathname.includes("/modeling")) {
      navigate("/modeling/overview");
    } else if (
      pathname.includes("view-option-buy-back") ||
      pathname.includes("start-buy-back") ||
      pathname.includes("create-option-buyback") ||
      pathname.includes("edit-options-buyback")
    ) {
      navigate("/options/optionBuyback");
    } else if (pathname.includes("add-edit-pool")) {
      navigate("/options/esopOverview");
    } else if (pathname.includes("rounds")) {
      navigate(`/rounds/allTransactions`);
    } else if (pathname.includes("rights/article")) {
      navigate("/rights/shareholder");
    } else if (pathname.includes("transaction"))
      navigate(`/transactions/allTransactions`);
    else if (pathname.includes("financials")) navigate(`/financials/overview`);
  };

  async function switchCompany(company: AssociatedCompany) {
    if (loading || company.uuid === user?.companyId) return;
    setLoading(true);
    setIsOpen(false);
    graphStore.setIsLoading(true);
    await queryClient.resetQueries("userDetails");
    shareholderInstrument.reset();
    transactionList.reset();
    switchCompanyApi(company.uuid)
      .then(async (_) => {
        sessionStorage.setItem("companyId", company.uuid);
        // Reflect the switch using what we already know about the target
        // company. A draft company has no cap table yet, so the backend
        // can't build a full userContext for it and the refetch below can
        // come back pointed at a different company — this optimistic
        // update (and the guard in useGetUserContext) keeps the UI showing
        // the company we actually switched to instead of that fallback.
        setAuth((prev: any) => ({
          companyId: company.uuid,
          user: prev.user
            ? {
                ...prev.user,
                companyId: company.uuid,
                nameOfTheCompany: company.nameOfTheCompany,
                isDraftCompany: company.isDraftCompany,
              }
            : prev.user,
        }));
        await queryClient.invalidateQueries();
        await queryClient.refetchQueries("userContext");
        setLoading(false);
        setEnable(false);
        graphStore.setIsLoading(false);
        goBack();
        setTimeout(() => {
          toast("Company switched", {
            type: "success",
            autoClose: 2000,
          });
        }, 2000);
      })
      .catch(async (err) => {
        queryClient.refetchQueries("userContext");
        toast("Error in switching company", {
          type: "error",
          autoClose: 2000,
        });
        graphStore.setIsLoading(false);
        setLoading(false);
        setEnable(false);
      });
  }

  useEffect(() => {
    function handleClickOutside(event: any) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [setIsOpen]);
  const { isMobile } = useIsMobile(1024);
  return (
    <div>
      {loading ? (
        <div className="items-center px-6 py-2">
          <p className="text-sm animate-pulse font-medium">Switching...</p>
        </div>
      ) : (
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`bg-gray-100 px-4 py-7 h-10 w-48 md:w-64 text-sm border-b  flex items-center justify-between ${
              user?.nameOfTheCompany ? "border-primary" : ""
            }`}
            type="button"
          >
            <span className="text-gray-700 whitespace-nowrap">
              {/* {selectedCompany ? ( */}
              <div
                className={`w-full ${
                  isMobile ? "max-w-[135px]" : "max-w-[200px]"
                } overflow-hidden`}
              >
                <p
                  className="text-start font-semibold truncate overflow-hidden whitespace-nowrap"
                  title={user?.nameOfTheCompany}
                >
                  {/* {selectedCompany.nameOfTheCompany} */}
                  {user?.nameOfTheCompany}
                </p>
                {companyData?.find((comp) => comp.uuid === user?.companyId)
                  ?.dba !== "" &&
                  companyData?.find((comp) => comp.uuid === user?.companyId)
                    ?.dba !== undefined && (
                    <p className="text-xxs text-start text-gray-500 truncate overflow-hidden whitespace-nowrap">
                      {companyData?.find(
                        (comp) => comp.uuid === user?.companyId
                      )?.dba !== "" &&
                      companyData?.find((comp) => comp.uuid === user?.companyId)
                        ?.dba !== undefined
                        ? `${
                            companyData?.find(
                              (comp) => comp.uuid === user?.companyId
                            )?.dba
                          }`
                        : ""}
                    </p>
                  )}
              </div>
            </span>

            <svg
              className={`w-5 h-5 transition-transform ${
                isOpen ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {isOpen && (
            <div className="absolute top-full -right-16 md:right-0 w-64 bg-white rounded-sm shadow-lg z-10 px-3 py-2">
              <div className="mb-2">
                <SearchInput
                  placeholder="Search Company"
                  onChange={(e: any) => setSearchTerm(e.target.value)}
                />
              </div>

              <div className="max-h-80 overflow-y-auto scroll-smooth">
                {filteredCompanies.map((company, index) => (
                  <div
                    key={company.uuid}
                    className={`p-2 px-1 hover:bg-gray-100 cursor-pointer ${
                      index !== filteredCompanies.length - 1
                        ? "border-b border-gray-200 border-dashed"
                        : ""
                    }`}
                    onClick={() => switchCompany(company)}
                  >
                    <p className="text-gray-700">{company.nameOfTheCompany}</p>
                    <p className="text-gray-400 text-xxs">{company.dba}</p>
                  </div>
                ))}
                {filteredCompanies.length === 0 && (
                  <label className="flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer">
                    <span className="text-gray-700 text-sm">
                      {"No Companies Available"}
                    </span>
                  </label>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
